import {
  DataGrid,
  formatCurrency,
  formatDate,
  StatusBadge
} from '@ijoin/components';
import { useGetEnrollmentRkTransactions } from '@ijoin/hooks';
import { Card, CardContent, CardHeader } from '@mui/material';

import { FC } from 'react';

export const EnrollmentRkTransactionsSection: FC<{
  planEnrollmentId: string;
  planId: string;
}> = props => {
  const transactions = useGetEnrollmentRkTransactions(
    props.planId,
    props.planEnrollmentId
  );

  if (!transactions.data?.length) {
    return null;
  }

  return (
    <Card sx={{ p: 1 }}>
      <CardHeader
        subheader='Transactions received from the record keeper'
        title='Record Keeper Transactions'
      />
      <CardContent>
        <DataGrid
          autoHeight={false}
          columns={[
            {
              field: 'postDate',
              flex: 1,
              headerName: 'Post Date',
              minWidth: 110,
              valueFormatter: value =>
                value
                  ? formatDate(value, { format: 'MM/DD/YYYY', useUtc: true })
                  : ''
            },
            {
              field: 'transType',
              flex: 1,
              headerName: 'Type',
              minWidth: 160,
              valueGetter: (value, row) =>
                [row.transTypeCode, value].filter(Boolean).join(' · ')
            },
            {
              field: 'transSubTypeCode',
              flex: 1,
              headerName: 'Sub Type',
              minWidth: 90
            },
            {
              field: 'buySellCode',
              flex: 1,
              headerName: 'Buy/Sell',
              minWidth: 90
            },
            {
              field: 'externalFundId',
              flex: 1,
              headerName: 'Fund',
              minWidth: 160
            },
            {
              field: 'contributionSourceId',
              flex: 1,
              headerName: 'Source',
              minWidth: 90
            },
            {
              align: 'center',
              field: 'isRoth',
              flex: 1,
              headerAlign: 'center',
              headerName: 'Roth',
              minWidth: 90,
              renderCell: params =>
                params.value ? (
                  <StatusBadge severity='info'>Roth</StatusBadge>
                ) : null
            },
            {
              field: 'numberOfUnits',
              flex: 1,
              headerName: 'Units',
              minWidth: 110,
              type: 'number'
            },
            {
              field: 'unitValue',
              flex: 1,
              headerName: 'Unit Value',
              minWidth: 110,
              type: 'number',
              valueFormatter: value =>
                value == null ? '' : formatCurrency(value)
            },
            {
              field: 'totalDollarAmt',
              flex: 1,
              headerName: 'Amount',
              minWidth: 120,
              type: 'number',
              valueFormatter: value =>
                value == null ? '' : formatCurrency(value)
            },
            {
              field: 'tradeDate',
              flex: 1,
              headerName: 'Trade Date',
              minWidth: 110,
              valueFormatter: value =>
                value
                  ? formatDate(value, { format: 'MM/DD/YYYY', useUtc: true })
                  : ''
            },
            {
              field: 'statusCode',
              flex: 1,
              headerName: 'Status',
              minWidth: 80
            },
            {
              align: 'center',
              field: 'reversalIndicator',
              flex: 1,
              headerAlign: 'center',
              headerName: 'Reversal',
              minWidth: 100,
              renderCell: params =>
                params.value ? (
                  <StatusBadge severity='warning'>Reversal</StatusBadge>
                ) : null
            },
            {
              field: 'dataSourceType',
              flex: 1,
              headerName: 'Source Type',
              minWidth: 110
            },
            {
              field: 'transId',
              flex: 1,
              headerName: 'Trans ID',
              minWidth: 110,
              type: 'number'
            },
            {
              field: 'seqNum',
              flex: 1,
              headerName: 'Seq',
              minWidth: 70,
              type: 'number'
            }
          ]}
          disableColumnMenu
          disableRowSelectionOnClick
          getRowId={row => row.id}
          loading={transactions.isFetching}
          pageSizeOptions={[25, 50, 100]}
          pinnedColumns={{ left: ['postDate'] }}
          rows={transactions.data}
          showColumnMenu
          slotProps={{
            root: {
              'data-component': 'dataGrid',
              'data-testid': 'enrollmentRkTransactions'
            }
          }}
          sx={{ maxHeight: 600 }}
        />
      </CardContent>
    </Card>
  );
};

EnrollmentRkTransactionsSection.displayName = 'EnrollmentRkTransactionsSection';
